import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderDeleteOutlinedIcon from '@mui/icons-material/FolderDeleteOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import DnsIcon from '@mui/icons-material/Dns';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchAiServerResourcesCurrent,
  fetchAiServerResourcesStats,
  downloadAiServerResourcesExport,
  countAiServerResourceSamples,
  deleteAiServerResourceSamples,
  triggerBrowserDownload,
  formatBytesLabel,
  formatMbps,
  formatSeriesBucketLabel,
  formatUptime,
  tempStatusLabel,
  type AiServerGpuItem,
  type AiServerResourceCurrent,
  type AiServerResourceStats,
  type AiServerSamplesDeleteParams,
} from '../../services/aiServerResourceService';
import {
  eventService,
  formatEvidenceBytes,
  type OrphanEvidencePurgeResult,
} from '../../services/eventService';

type Period = 'day' | 'month';

/** 기본: 현재값+통계 함께 갱신 */
const POLL_NORMAL_MS = 15_000;
/** 빠른 갱신: 현재값만 (잠깐 부하 확인용) */
const POLL_FAST_MS = 3_000;
/** 빠른 갱신 중에도 통계/차트는 이 주기로만 */
const POLL_STATS_WHILE_FAST_MS = 60_000;
const FAST_POLL_LS_KEY = 'aiServerResourceFastPoll';

const GPU_LINE_COLORS = ['#ed6c02', '#d32f2f', '#0288d1', '#7b1fa2', '#00695c', '#5d4037'];

function usageColor(pct: number): 'success' | 'warning' | 'error' | 'primary' {
  if (pct >= 90) return 'error';
  if (pct >= 75) return 'warning';
  return 'primary';
}

function tempColor(c?: number | null): 'success' | 'warning' | 'error' | 'primary' | 'inherit' {
  if (c == null || Number.isNaN(c)) return 'inherit';
  if (c >= 90) return 'error';
  if (c >= 80) return 'error';
  if (c >= 65) return 'warning';
  return 'success';
}

function tempChipColor(
  status?: string
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (status === 'ok') return 'success';
  if (status === 'warm') return 'warning';
  if (status === 'hot' || status === 'critical') return 'error';
  return 'default';
}

function kindLabel(kind?: string): string {
  if (kind === 'integrated') return '내장';
  if (kind === 'discrete') return '외장';
  return '';
}

function shortGpuName(name?: string, max = 28): string {
  const n = (name || 'GPU').trim();
  return n.length > max ? `${n.slice(0, max - 1)}…` : n;
}

const GaugeCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  percent?: number | null;
  detail?: string;
  sub?: string;
  chips?: React.ReactNode;
}> = ({ title, icon, percent, detail, sub, chips }) => {
  const pct = percent == null || Number.isNaN(percent) ? null : Math.max(0, Math.min(100, percent));
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          {icon}
          <Typography variant="subtitle1" fontWeight={600} noWrap title={title}>
            {title}
          </Typography>
        </Stack>
        {chips}
        <Typography variant="h4" component="div" sx={{ mb: 1, mt: chips ? 1 : 0 }}>
          {pct == null ? '-' : `${pct.toFixed(1)}%`}
        </Typography>
        {pct != null && (
          <LinearProgress
            variant="determinate"
            value={pct}
            color={usageColor(pct)}
            sx={{ height: 8, borderRadius: 1, mb: 1 }}
          />
        )}
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
        {sub && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const TempCard: React.FC<{
  title: string;
  celsius?: number | null;
  status?: string;
  detail?: string;
}> = ({ title, celsius, status, detail }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <ThermostatIcon color={tempColor(celsius) === 'inherit' ? 'action' : tempColor(celsius)} />
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {title}
        </Typography>
        <Chip size="small" label={tempStatusLabel(status)} color={tempChipColor(status)} />
      </Stack>
      <Typography variant="h4" component="div" sx={{ mb: 0.5 }}>
        {celsius == null ? '-' : `${celsius.toFixed(1)}°C`}
      </Typography>
      {detail && (
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      )}
    </CardContent>
  </Card>
);

function gpuCardDetail(g: AiServerGpuItem): string {
  const parts: string[] = [];
  if (g.mem_used_mb != null && g.mem_total_mb != null) {
    parts.push(`VRAM ${g.mem_used_mb} / ${g.mem_total_mb} MB`);
  } else if (g.mem_total_mb != null) {
    parts.push(`메모리 ~${g.mem_total_mb} MB`);
  }
  if (g.temperature_c != null) parts.push(`${g.temperature_c}°C`);
  if (g.util_percent == null && g.source && g.source !== 'nvidia-smi') {
    parts.push('사용률 미제공(OS 목록)');
  }
  return parts.join(' · ') || g.name || '';
}

const AiServerResourceMonitor: React.FC = () => {
  const { authState } = useAuth();
  const isSuperuser = Boolean(authState.user?.is_superuser);
  const [current, setCurrent] = useState<AiServerResourceCurrent | null>(null);
  const [stats, setStats] = useState<AiServerResourceStats | null>(null);
  const [period, setPeriod] = useState<Period>('day');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [fastPoll, setFastPoll] = useState(() => {
    try {
      return localStorage.getItem(FAST_POLL_LS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [deleteMode, setDeleteMode] = useState<'range' | 'older'>('older');
  const [deleteAfter, setDeleteAfter] = useState('');
  const [deleteBefore, setDeleteBefore] = useState('');
  const [deleteOlderDays, setDeleteOlderDays] = useState(40);
  const [deletePreviewCount, setDeletePreviewCount] = useState<number | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

  // 미연결 증거 정리
  const [orphanPreview, setOrphanPreview] = useState<OrphanEvidencePurgeResult | null>(null);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [orphanMessage, setOrphanMessage] = useState<string | null>(null);

  const loadCurrent = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const cur = await fetchAiServerResourcesCurrent();
      setCurrent(cur);
      setUpdatedAt(new Date().toLocaleString());
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : 'AI 서버 자원 정보를 불러오기 실패';
      setError(msg);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const st = await fetchAiServerResourcesStats(period);
      setStats(st);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : '통계를 불러오기 실패';
      setError(msg);
    }
  }, [period]);

  const loadAll = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      setError(null);
      try {
        const [cur, st] = await Promise.all([
          fetchAiServerResourcesCurrent(),
          fetchAiServerResourcesStats(period),
        ]);
        setCurrent(cur);
        setStats(st);
        setUpdatedAt(new Date().toLocaleString());
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message?: string }).message)
            : 'AI 서버 자원 정보를 불러오기 실패';
        setError(msg);
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [period]
  );

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (fastPoll) {
      const curTimer = window.setInterval(() => {
        void loadCurrent({ silent: true });
      }, POLL_FAST_MS);
      const statsTimer = window.setInterval(() => {
        void loadStats();
      }, POLL_STATS_WHILE_FAST_MS);
      return () => {
        window.clearInterval(curTimer);
        window.clearInterval(statsTimer);
      };
    }
    const t = window.setInterval(() => {
      void loadAll({ silent: true });
    }, POLL_NORMAL_MS);
    return () => window.clearInterval(t);
  }, [fastPoll, loadAll, loadCurrent, loadStats]);

  const handleFastPollChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFastPoll(checked);
    try {
      localStorage.setItem(FAST_POLL_LS_KEY, checked ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const handleDownload = useCallback(
    async (format: 'csv' | 'json' | 'pdf') => {
      setDownloading(true);
      setError(null);
      try {
        if (format === 'pdf') {
          const { saveAiServerResourcesPdf } = await import(
            '../../services/aiServerResourcePdfExport'
          );
          await saveAiServerResourcesPdf(period);
        } else {
          const { blob, filename } = await downloadAiServerResourcesExport(period, format);
          triggerBrowserDownload(blob, filename);
        }
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message?: string }).message)
            : '통계 다운로드에 실패';
        setError(msg);
      } finally {
        setDownloading(false);
      }
    },
    [period]
  );

  const buildDeleteParams = useCallback((): AiServerSamplesDeleteParams | null => {
    if (deleteMode === 'older') {
      const days = Math.floor(Number(deleteOlderDays));
      if (!Number.isFinite(days) || days < 1) return null;
      return { olderThanDays: days };
    }
    const after = deleteAfter.trim() || undefined;
    const before = deleteBefore.trim() || undefined;
    if (!after && !before) return null;
    return { after, before };
  }, [deleteMode, deleteOlderDays, deleteAfter, deleteBefore]);

  const handlePreviewDeleteCount = useCallback(async () => {
    const params = buildDeleteParams();
    if (!params) {
      setError('삭제 기간을 지정. (날짜 범위 또는 N일보다 오래된 자료)');
      setDeletePreviewCount(null);
      return;
    }
    setDeleteBusy(true);
    setError(null);
    setDeleteMessage(null);
    try {
      const res = await countAiServerResourceSamples(params);
      setDeletePreviewCount(typeof res.count === 'number' ? res.count : 0);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : '삭제 대상 건수를 조회실패';
      setError(msg);
      setDeletePreviewCount(null);
    } finally {
      setDeleteBusy(false);
    }
  }, [buildDeleteParams]);

  const handleConfirmDelete = useCallback(async () => {
    const params = buildDeleteParams();
    if (!params) {
      setError('삭제 기간을 지정');
      setDeleteConfirmOpen(false);
      return;
    }
    setDeleteBusy(true);
    setError(null);
    setDeleteMessage(null);
    try {
      const res = await deleteAiServerResourceSamples(params);
      const n = typeof res.deleted === 'number' ? res.deleted : 0;
      setDeleteMessage(`${n.toLocaleString()}건의 자원 샘플을 삭제함`);
      setDeletePreviewCount(null);
      setDeleteConfirmOpen(false);
      await loadAll({ silent: true });
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : '샘플 삭제에 실패. 최고관리자 권한 필요 가능';
      setError(msg);
      setDeleteConfirmOpen(false);
    } finally {
      setDeleteBusy(false);
    }
  }, [buildDeleteParams, loadAll]);

  const handleOpenOrphanPreview = useCallback(async () => {
    setOrphanBusy(true);
    setError(null);
    setOrphanMessage(null);
    try {
      const preview = await eventService.previewOrphanEvidence();
      setOrphanPreview(preview);
      setOrphanDialogOpen(true);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : '미연결 증거 조회에 실패';
      setError(msg);
    } finally {
      setOrphanBusy(false);
    }
  }, []);

  const handleConfirmOrphanPurge = useCallback(async () => {
    setOrphanBusy(true);
    setError(null);
    try {
      const result = await eventService.purgeOrphanEvidence();
      const dirs = result.deleted_dirs ?? 0;
      const bytes = formatEvidenceBytes(result.deleted_bytes ?? 0);
      setOrphanMessage(`미연결 증거 ${dirs}개 폴더(${bytes})를 삭제함`);
      setOrphanDialogOpen(false);
      setOrphanPreview(null);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message)
          : '미연결 증거 삭제에 실패';
      setError(msg);
      setOrphanDialogOpen(false);
    } finally {
      setOrphanBusy(false);
    }
  }, []);

  const gpuList: AiServerGpuItem[] = current?.gpu?.gpus ?? [];

  const perGpuMeta = useMemo(() => {
    if (stats?.per_gpu && stats.per_gpu.length > 0) return stats.per_gpu;
    return gpuList.map((g, i) => ({
      id: g.id || `gpu${i}`,
      name: g.name || `GPU ${i}`,
      vendor: g.vendor,
      kind: g.kind,
      summary: {
        util: { avg: null, max: null, min: null, count: 0 },
        memory: { avg: null, max: null, min: null, count: 0 },
      },
    }));
  }, [stats, gpuList]);

  const chartData = useMemo(() => {
    const bucketKind = stats?.bucket;
    return (stats?.series || []).map((row) => {
      const point: Record<string, string | number | null> = {
        name: formatSeriesBucketLabel(row.bucket, bucketKind),
        bucketFull: row.bucket,
        CPU: row.cpu_avg,
        RAM: row.mem_avg,
        Disk: row.disk_avg,
        CPU온도: row.cpu_temp_avg ?? null,
        GPU온도: row.gpu_temp_avg ?? null,
        Net송신: row.net_send_mbps_avg ?? null,
        Net수신: row.net_recv_mbps_avg ?? null,
        Net부하: row.net_load_avg ?? null,
      };
      for (const g of perGpuMeta) {
        const v = row.gpus?.[g.id];
        point[g.id] = v ?? null;
      }
      return point;
    });
  }, [stats, perGpuMeta]);

  const periodAverages = stats?.averages ?? null;

  const thermalSensors = current?.thermal?.sensors ?? [];
  const network = current?.network;
  const summary = stats?.summary;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="h6">AI 서버 자원 모니터링</Typography>
          <Typography variant="body2" color="text.secondary">
            백엔드가 실행 중인 AI 서버 호스트 기준. 접속 PC(브라우저) 자원 아님.
            {fastPoll
              ? ' · 빠른 갱신: 현재값 3초 / 통계·차트 60초'
              : ' · 자동 갱신: 15초 (현재값·통계)'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={fastPoll}
                onChange={handleFastPollChange}
                color="warning"
              />
            }
            label={
              <Typography variant="body2">
                빠른 갱신 (3초)
              </Typography>
            }
            sx={{ mr: 1, ml: 0 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="ai-res-period">통계 기간</InputLabel>
            <Select
              labelId="ai-res-period"
              label="통계 기간"
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
            >
              <MenuItem value="day">일별 (24시간)</MenuItem>
              <MenuItem value="month">월별 (30일)</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={downloading || loading}
            onClick={() => void handleDownload('csv')}
          >
            CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={downloading || loading}
            onClick={() => void handleDownload('json')}
          >
            JSON
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={downloading || loading}
            onClick={() => void handleDownload('pdf')}
          >
            PDF
          </Button>
          <Tooltip title="새로고침">
            <span>
              <IconButton onClick={() => void loadAll()} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {deleteMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDeleteMessage(null)}>
          {deleteMessage}
        </Alert>
      )}
      {orphanMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOrphanMessage(null)}>
          {orphanMessage}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* ── 수집 자료(샘플) 삭제 ── */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardHeader
              title="수집 자료 삭제"
              subheader="기간 지정 AI 서버 자원 샘플 삭제 (최고관리자)"
              avatar={<DeleteOutlineIcon color="action" />}
            />
            <CardContent sx={{ pt: 0 }}>
              <RadioGroup
                row
                value={deleteMode}
                onChange={(_, v) => {
                  setDeleteMode(v as 'range' | 'older');
                  setDeletePreviewCount(null);
                }}
              >
                <FormControlLabel value="older" control={<Radio size="small" />} label="N일보다 오래된 자료" />
                <FormControlLabel value="range" control={<Radio size="small" />} label="날짜 범위" />
              </RadioGroup>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
                flexWrap="wrap"
                useFlexGap
                mt={1}
              >
                {deleteMode === 'older' ? (
                  <TextField
                    size="small"
                    type="number"
                    label="일수"
                    value={deleteOlderDays}
                    onChange={(e) => {
                      setDeleteOlderDays(Number(e.target.value));
                      setDeletePreviewCount(null);
                    }}
                    inputProps={{ min: 1, max: 3650 }}
                    sx={{ width: 120 }}
                    helperText="예: 40 → 40일 이전 샘플"
                  />
                ) : (
                  <>
                    <TextField
                      size="small"
                      type="date"
                      label="시작일"
                      value={deleteAfter}
                      onChange={(e) => {
                        setDeleteAfter(e.target.value);
                        setDeletePreviewCount(null);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 160 }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="종료일"
                      value={deleteBefore}
                      onChange={(e) => {
                        setDeleteBefore(e.target.value);
                        setDeletePreviewCount(null);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 160 }}
                    />
                  </>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={deleteBusy}
                  onClick={() => void handlePreviewDeleteCount()}
                >
                  건수 확인
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={deleteBusy || !isSuperuser}
                  onClick={() => {
                    const params = buildDeleteParams();
                    if (!params) {
                      setError('삭제 기간을 지정');
                      return;
                    }
                    setDeleteConfirmOpen(true);
                  }}
                >
                  삭제
                </Button>
                {deletePreviewCount != null && (
                  <Typography variant="body2" color="text.secondary">
                    대상: {deletePreviewCount.toLocaleString()}건
                  </Typography>
                )}
                {!isSuperuser && (
                  <Typography variant="caption" color="text.secondary">
                    최고관리자만 실행 가능
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── 미연결 증거 정리 ── */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardHeader
              title="미연결 증거 정리"
              subheader="이벤트 목록에 없는 _staging_* 및 삭제된 이벤트 폴더를 일괄 삭제."
              avatar={<FolderDeleteOutlinedIcon color="action" />}
            />
            <CardContent sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                현재 이벤트 리스트(DB)에 연결된 증거 폴더는 그대로 유지되고,
                미연결 <code>_staging_*</code>·삭제된 이벤트 폴더만 디스크에서 제거됨.
                최근 10분 이내 스테이징은 보호됨
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  disabled={orphanBusy}
                  startIcon={
                    orphanBusy ? <CircularProgress size={14} /> : <FolderDeleteOutlinedIcon />
                  }
                  onClick={() => void handleOpenOrphanPreview()}
                >
                  미리보기 및 삭제
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── 미연결 증거 삭제 확인 다이얼로그 ── */}
      <Dialog
        open={orphanDialogOpen}
        onClose={() => !orphanBusy && setOrphanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>미연결 증거 일괄 정리</DialogTitle>
        <DialogContent>
          {orphanPreview && (
            <Box>
              <Typography variant="body2">
                유지 이벤트: {(orphanPreview.kept_event_ids ?? 0).toLocaleString()}건 ·
                유지 폴더: {(orphanPreview.kept_dirs ?? 0).toLocaleString()}개
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                삭제 대상: {(orphanPreview.orphan_dirs ?? 0).toLocaleString()}개 폴더 (
                {formatEvidenceBytes(orphanPreview.orphan_bytes ?? 0)})
              </Typography>
              {(orphanPreview.skipped_recent_staging ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  최근 스테이징 보호: {orphanPreview.skipped_recent_staging}개
                </Typography>
              )}
              {(orphanPreview.samples?.length ?? 0) > 0 && (
                <List dense sx={{ maxHeight: 180, overflow: 'auto', mt: 1 }}>
                  {orphanPreview.samples!.slice(0, 15).map((s, i) => (
                    <ListItem key={s.path || i} disableGutters sx={{ py: 0 }}>
                      <ListItemText
                        primary={`[${s.kind}] ${s.name}`}
                        secondary={formatEvidenceBytes(s.bytes ?? 0)}
                        primaryTypographyProps={{ variant: 'caption' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                  {(orphanPreview.orphan_dirs ?? 0) > 15 && (
                    <ListItem disableGutters sx={{ py: 0 }}>
                      <ListItemText
                        primary={`… 외 ${((orphanPreview.orphan_dirs ?? 0) - 15).toLocaleString()}개`}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
              {(orphanPreview.orphan_dirs ?? 0) === 0 && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  삭제할 미연결 증거 없음
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrphanDialogOpen(false)} disabled={orphanBusy}>
            취소
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={orphanBusy || (orphanPreview?.orphan_dirs ?? 0) === 0}
            onClick={() => void handleConfirmOrphanPurge()}
          >
            {orphanBusy ? '삭제 중…' : '삭제 실행'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !deleteBusy && setDeleteConfirmOpen(false)}>
        <DialogTitle>수집 자료 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteMode === 'older'
              ? `${deleteOlderDays}일보다 오래된 자원 샘플을 삭제`
              : `선택한 기간(${deleteAfter || '…'} ~ ${deleteBefore || '…'})의 자원 샘플을 삭제`}
            {deletePreviewCount != null
              ? ` 예상 ${deletePreviewCount.toLocaleString()}건`
              : ' 대상 건수를 먼저 확인해 두는 것을 권장'}
            {' '}이 작업은 되돌리기 불가.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteBusy}>
            취소
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteBusy}
            onClick={() => void handleConfirmDelete()}
          >
            {deleteBusy ? '삭제 중…' : '삭제 실행'}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && !current ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
            <Chip size="small" color="info" label={`호스트: ${current?.hostname || '-'}`} />
            <Chip size="small" label={`업타임: ${formatUptime(current?.uptime_seconds)}`} />
            <Chip size="small" label={`프로세스: ${current?.process_count ?? '-'}`} />
            <Chip size="small" label={`GPU ${gpuList.length}개`} />
            {stats?.bucket_timezone && (
              <Chip
                size="small"
                variant="outlined"
                label={`그래프 시간: ${stats.bucket_timezone} (AI 서버 로컬)`}
              />
            )}
            {fastPoll && (
              <Chip size="small" color="warning" label="빠른 갱신 ON" />
            )}
            {updatedAt && <Chip size="small" variant="outlined" label={`갱신: ${updatedAt}`} />}
          </Stack>

          {periodAverages && (stats?.sample_count ?? 0) > 0 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title="항목별 기간 평균"
                subheader={
                  period === 'day'
                    ? '최근 24시간 샘플 집계'
                    : '최근 30일 샘플 집계'
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={1}>
                  {[
                    { label: 'CPU', v: periodAverages.cpu_percent, unit: '%' },
                    { label: 'RAM', v: periodAverages.memory_percent, unit: '%' },
                    { label: '디스크', v: periodAverages.disk_percent, unit: '%' },
                    { label: 'GPU 사용', v: periodAverages.gpu_util_percent, unit: '%' },
                    { label: 'GPU VRAM', v: periodAverages.gpu_memory_percent, unit: '%' },
                    { label: 'CPU 온도', v: periodAverages.cpu_temp_c, unit: '°C' },
                    { label: 'GPU 온도', v: periodAverages.gpu_temp_c, unit: '°C' },
                    {
                      label: 'Net 송신',
                      v: periodAverages.net_send_mbps,
                      unit: ' Mbps',
                      fmt: formatMbps,
                    },
                    {
                      label: 'Net 수신',
                      v: periodAverages.net_recv_mbps,
                      unit: ' Mbps',
                      fmt: formatMbps,
                    },
                    { label: 'Net 부하', v: periodAverages.net_load_percent, unit: '%' },
                  ].map((row) => (
                    <Grid item xs={6} sm={4} md={3} key={row.label}>
                      <Typography variant="caption" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {row.fmt
                          ? row.fmt(row.v as number | null | undefined)
                          : row.v == null || Number.isNaN(Number(row.v))
                            ? '-'
                            : `${Number(row.v).toFixed(2)}${row.unit}`}
                      </Typography>
                    </Grid>
                  ))}
                  {perGpuMeta.map((g) => {
                    const key = `gpu_${g.id}_util_percent`;
                    const v = periodAverages[key as keyof typeof periodAverages];
                    return (
                      <Grid item xs={6} sm={4} md={3} key={key}>
                        <Typography variant="caption" color="text.secondary" noWrap title={g.name}>
                          {shortGpuName(g.name, 20)} 평균
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {v == null ? '-' : `${Number(v).toFixed(2)}%`}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <GaugeCard
                title="CPU"
                icon={<MemoryIcon color="primary" />}
                percent={current?.cpu?.percent}
                detail={
                  current?.cpu
                    ? `논리 ${current.cpu.count_logical ?? '-'} / 물리 ${
                        current.cpu.count_physical ?? '-'
                      }코어`
                    : undefined
                }
                sub={
                  current?.cpu?.load_avg_1m != null
                    ? `load 1m: ${current.cpu.load_avg_1m.toFixed(2)}`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <GaugeCard
                title="RAM"
                icon={<DnsIcon color="primary" />}
                percent={current?.memory?.percent}
                detail={
                  current?.memory
                    ? `${current.memory.used_gb ?? '-'} / ${current.memory.total_gb ?? '-'} GB`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <GaugeCard
                title="SSD/디스크"
                icon={<StorageIcon color="primary" />}
                percent={current?.disk?.percent}
                detail={
                  current?.disk
                    ? `${current.disk.used_gb ?? '-'} / ${current.disk.total_gb ?? '-'} GB (${
                        current.disk.path || '/'
                      })`
                    : undefined
                }
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            GPU (장치별)
          </Typography>
          {gpuList.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              감지된 GPU가 없음. NVIDIA는 nvidia-smi, 내장 GPU는 OS 목록으로 수집.
            </Alert>
          ) : (
            <Grid container spacing={2} mb={2}>
              {gpuList.map((g, i) => {
                const title = `GPU${g.index ?? i} · ${shortGpuName(g.name)}`;
                const k = kindLabel(g.kind);
                return (
                  <Grid item xs={12} sm={6} md={4} key={g.id || `${g.index}-${g.name}`}>
                    <GaugeCard
                      title={title}
                      icon={<DeveloperBoardIcon color="primary" />}
                      percent={g.util_percent}
                      detail={gpuCardDetail(g)}
                      sub={g.name}
                      chips={
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {k && (
                            <Chip
                              size="small"
                              label={k}
                              color={k === '내장' ? 'default' : 'warning'}
                            />
                          )}
                          {g.vendor && <Chip size="small" variant="outlined" label={g.vendor} />}
                          {g.source && (
                            <Chip size="small" variant="outlined" label={g.source} />
                          )}
                        </Stack>
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}

          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            발열 상태
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TempCard
                title="CPU 온도"
                celsius={current?.thermal?.cpu_celsius ?? current?.cpu?.temp_celsius}
                status={current?.thermal?.cpu_status ?? current?.cpu?.temp_status}
                detail={
                  current?.thermal?.cpu_celsius_avg != null
                    ? `센서 평균 ${current.thermal.cpu_celsius_avg}°C`
                    : 'Linux: sensors / Windows: ACPI(지원 시)'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TempCard
                title="GPU 최고 온도"
                celsius={current?.thermal?.gpu_celsius_max}
                status={current?.thermal?.gpu_status}
                detail={
                  current?.thermal?.gpu_celsius_avg != null
                    ? `GPU 평균 ${current.thermal.gpu_celsius_avg}°C`
                    : 'nvidia-smi 온도'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    센서 목록
                  </Typography>
                  {thermalSensors.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      온도 센서가 없거나 OS에서 제공하지 않음
                    </Typography>
                  ) : (
                    <Stack spacing={0.5} sx={{ maxHeight: 140, overflow: 'auto' }}>
                      {thermalSensors.slice(0, 12).map((s) => (
                        <Stack
                          key={s.id || s.label}
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography variant="caption" noWrap title={s.label}>
                            [{s.component}] {s.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              s.celsius == null ? '-' : `${s.celsius}°C · ${tempStatusLabel(s.status)}`
                            }
                            color={tempChipColor(s.status)}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            네트워크 부하
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <GaugeCard
                title="송신 부하"
                icon={<NetworkCheckIcon color="primary" />}
                percent={network?.send_load_percent}
                detail={formatMbps(network?.send_mbps)}
                sub={`기준 링크 ${network?.link_ref_mbps ?? 1000} Mbps`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GaugeCard
                title="수신 부하"
                icon={<NetworkCheckIcon color="primary" />}
                percent={network?.recv_load_percent}
                detail={formatMbps(network?.recv_mbps)}
                sub={`부팅 이후 누적 수신 ${formatBytesLabel(network?.bytes_recv)}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <GaugeCard
                title="합산 부하"
                icon={<NetworkCheckIcon color="primary" />}
                percent={network?.total_load_percent}
                detail={`↑ ${formatMbps(network?.send_mbps)} / ↓ ${formatMbps(network?.recv_mbps)}`}
                sub={`부팅 이후 누적 송신 ${formatBytesLabel(network?.bytes_sent)}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    NIC
                  </Typography>
                  {(network?.nics?.length ?? 0) === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      인터페이스 정보 없음
                    </Typography>
                  ) : (
                    <Stack spacing={0.5} sx={{ maxHeight: 140, overflow: 'auto' }}>
                      {network!.nics!.slice(0, 8).map((n) => (
                        <Typography key={n.name} variant="caption" display="block" noWrap>
                          {n.is_up === false ? '↓' : '↑'} {n.name}
                          {n.speed_mbps ? ` · ${n.speed_mbps}Mbps` : ''} · RX{' '}
                          {formatBytesLabel(n.bytes_recv)}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {(current?.disk?.partitions?.length ?? 0) > 1 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader title="디스크 파티션" />
              <CardContent sx={{ pt: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>경로</TableCell>
                      <TableCell>FS</TableCell>
                      <TableCell align="right">사용</TableCell>
                      <TableCell align="right">용량</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {current!.disk.partitions!.map((p) => (
                      <TableRow key={p.path}>
                        <TableCell>{p.path}</TableCell>
                        <TableCell>{p.fstype || '-'}</TableCell>
                        <TableCell align="right">{p.percent}%</TableCell>
                        <TableCell align="right">
                          {p.used_gb ?? '-'} / {p.total_gb ?? '-'} GB
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardHeader
                  title={period === 'day' ? '일별 누적 요약 (24h)' : '월별 누적 요약 (30일)'}
                  subheader={`샘플 ${stats?.sample_count ?? 0}건 · 약 1분 간격 수집`}
                />
                <CardContent sx={{ pt: 0 }}>
                  {!summary || (stats?.sample_count ?? 0) === 0 ? (
                    <Alert severity="info">
                      아직 저장된 샘플 없음. 서버 기동 후 수 분 뒤부터 그래프가 채워짐.
                    </Alert>
                  ) : (
                    <Stack spacing={1.2}>
                      <Typography variant="body2">
                        CPU 평균/최대: <b>{summary.cpu.avg ?? '-'}%</b> /{' '}
                        <b>{summary.cpu.max ?? '-'}%</b>
                      </Typography>
                      <Typography variant="body2">
                        RAM 평균/최대: <b>{summary.memory.avg ?? '-'}%</b> /{' '}
                        <b>{summary.memory.max ?? '-'}%</b>
                      </Typography>
                      <Typography variant="body2">
                        디스크 평균/최대: <b>{summary.disk.avg ?? '-'}%</b> /{' '}
                        <b>{summary.disk.max ?? '-'}%</b>
                      </Typography>
                      {perGpuMeta.map((g) => (
                        <Typography variant="body2" key={g.id}>
                          {shortGpuName(g.name, 22)} 평균/최대:{' '}
                          <b>{g.summary.util.avg ?? '-'}%</b> / <b>{g.summary.util.max ?? '-'}%</b>
                        </Typography>
                      ))}
                      <Typography variant="body2">
                        CPU 온도 평균/최대:{' '}
                        <b>{summary.cpu_temp?.avg ?? '-'}°C</b> /{' '}
                        <b>{summary.cpu_temp?.max ?? '-'}°C</b>
                      </Typography>
                      <Typography variant="body2">
                        GPU 온도 평균/최대:{' '}
                        <b>{summary.gpu_temp?.avg ?? '-'}°C</b> /{' '}
                        <b>{summary.gpu_temp?.max ?? '-'}°C</b>
                      </Typography>
                      <Typography variant="body2">
                        Net 송신/수신 평균:{' '}
                        <b>{formatMbps(summary.net_send_mbps?.avg)}</b> /{' '}
                        <b>{formatMbps(summary.net_recv_mbps?.avg)}</b>
                      </Typography>
                      <Typography variant="body2">
                        Net 부하 평균/최대:{' '}
                        <b>{summary.net_load?.avg ?? '-'}%</b> /{' '}
                        <b>{summary.net_load?.max ?? '-'}%</b>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        고부하 누적(추정): CPU≥80% 약 {summary.approx_high_cpu_minutes}분 ·
                        RAM≥85% 약 {summary.approx_high_mem_minutes}분
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardHeader
                  title={period === 'day' ? '일별 추이 (시간 평균)' : '월별 추이 (일 평균)'}
                  subheader={`AI 서버 로컬 시각${stats?.bucket_timezone ? ` · ${stats.bucket_timezone}` : ''} · GPU는 장치별 선`}
                />
                <CardContent sx={{ pt: 0, height: 340 }}>
                  {chartData.length === 0 ? (
                    <Box height={280} display="flex" alignItems="center" justifyContent="center">
                      <Typography color="text.secondary">표시할 시계열 데이터 없음.</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="CPU" stroke="#1976d2" dot={false} strokeWidth={2} connectNulls />
                        <Line type="monotone" dataKey="RAM" stroke="#9c27b0" dot={false} strokeWidth={2} connectNulls />
                        <Line type="monotone" dataKey="Disk" stroke="#2e7d32" dot={false} strokeWidth={2} connectNulls />
                        <Line type="monotone" dataKey="Net부하" stroke="#455a64" dot={false} strokeWidth={2} connectNulls />
                        {perGpuMeta.map((g, i) => (
                          <Line
                            key={g.id}
                            type="monotone"
                            dataKey={g.id}
                            name={shortGpuName(g.name, 18)}
                            stroke={GPU_LINE_COLORS[i % GPU_LINE_COLORS.length]}
                            dot={false}
                            strokeWidth={2}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="발열 추이 (°C)" />
                <CardContent sx={{ pt: 0, height: 280 }}>
                  {chartData.length === 0 ? (
                    <Box height={220} display="flex" alignItems="center" justifyContent="center">
                      <Typography color="text.secondary">데이터 없음</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit="°C" />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="CPU온도" stroke="#c62828" dot={false} strokeWidth={2} connectNulls />
                        <Line type="monotone" dataKey="GPU온도" stroke="#ef6c00" dot={false} strokeWidth={2} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="네트워크 처리량 (Mbps)" />
                <CardContent sx={{ pt: 0, height: 280 }}>
                  {chartData.length === 0 ? (
                    <Box height={220} display="flex" alignItems="center" justifyContent="center">
                      <Typography color="text.secondary">데이터 없음</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} unit="M" />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="Net송신" stroke="#1565c0" dot={false} strokeWidth={2} connectNulls />
                        <Line type="monotone" dataKey="Net수신" stroke="#00838f" dot={false} strokeWidth={2} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AiServerResourceMonitor;
